import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Metadata from '../../../components/Metadata/Metadata';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import { Box, Typography } from '@mui/material';
import Section from '../../../components/Section/Section';
import Accordion from '../../../components/Accordion/Accordion';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import Image from '../../../components/Image/Image';
import ListItem from '../../../components/ListItem/ListItem';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import Button from '../../../components/Button/Button';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Government Industries Solutions | General Genomics"
        description="Explore General Genomics’ tailored data solutions for federal, state, and public sector agencies. Discover secure, scalable applications in bioinformatics, public health, and national security."
      />
      <Hero
        helper="Industries &gt; Government"
        headline="Supporting government innovation with secure and <br/> <span class='nowrap'>scalable data</span>"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Empowering Government with Data
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics delivers secure, scalable, and compliant
          data solutions tailored for federal, state, and public sector
          agencies. Our specialized offerings empower decision-makers with
          cutting-edge bioinformatics, public health analytics, national
          security insights, and transformative public sector solutions. From
          ensuring compliance with HIPAA and GDPR standards to leveraging
          innovative AI-driven analytics, we are your trusted partner in
          navigating complex data challenges. Contact us to see how our
          solutions enhance operational efficiency, support policy decisions,
          and drive impactful outcomes.
        </p>
        <Image
          path="/images/library/soldiers-sitting.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-meeting-4.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Key Features</p>
        <h2 className="action-on-view action-fade-in">
          Tailored Data Solutions for Government
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Advanced Bioinformatics:</strong> Unlock deeper insights
            from complex biological data to support health initiatives and
            research.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Public Health Analytics:</strong> Leverage real-time data
            for better-informed policy decisions and resource allocation.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>National Security Solutions:</strong> Enhance intelligence
            capabilities with secure, scalable analytics tools designed for
            mission-critical tasks.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Government Compliance Expertise:</strong> Ensure seamless
            adherence to HIPAA, GDPR, and other regulatory standards.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>AI-Driven Innovation:</strong> Transform data workflows with
            artificial intelligence, synthetic data (SYN46™), and predictive
            analytics.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About Our Genomics Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics is advancing genomics research, with
          streamlined procurement, secure data handling, and innovative
          solutions. Explore the pages below to learn more!
        </p>
        <div className="card-group">
          <Card
            icon="security"
            headline="Data Governance & Compliance"
            subhead="Explore our commitment to secure and compliant data management."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="approval"
            headline="Innovation"
            subhead="Discover our groundbreaking solutions like AI-driven analytics and synthetic data (SYN46™)."
            btnURL="/innovation"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="storage"
            headline="Data Portal"
            subhead="Access data solutions tailored to government needs."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/lab-scientist-military-2.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; government
          data solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover how General Genomics supports federal, state, and public
          sector agencies with secure, scalable data solutions and compliance.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What data solutions does General Genomics offer for government agencies?"
          >
            General Genomics provides specialized solutions for federal, state,
            and public sector agencies, including{' '}
            <Link to="/industries/government/bioinformatics">
              bioinformatics,
            </Link>{' '}
            public health data analytics, and{' '}
            <Link to="/industries/government/national-security">
              national security
            </Link>{' '}
            applications, ensuring secure, scalable, and compliant data
            management.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure data compliance with government standards?"
          >
            We adhere to stringent government-specific standards such as HIPAA
            and GDPR to ensure that all data is handled securely and in
            compliance with applicable regulations.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can General Genomics help with public health data analytics?"
          >
            Yes, we offer tailored public health data analytics solutions to
            support government agencies in making data-driven decisions for
            public health initiatives and improving outcomes.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What sets General Genomics apart in supporting national security?"
          >
            Our solutions are designed to meet the unique challenges of{' '}
            <Link to="/industries/government/national-security">
              national security,
            </Link>{' '}
            providing secure, scalable data analysis tools that ensure the
            protection and integrity of sensitive information.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can my agency contract or access General Genomcis' solutions?"
          >
            Our streamlined procurement process and tailored data solutions make
            it easy for government agencies to partner with us.{' '}
            <Link to="/contact-us">Contact us</Link> today to explore our
            offerings and discuss how we can support your agency&apos;s needs.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
